import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { userAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { getAvatarFallback } from '../lib/utils';
import { toast } from 'sonner';

export function EditProfilePage() {
  const { user, updateUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [picture, setPicture] = useState(user?.picture || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {};
      if (name !== user?.name) data.name = name;
      if (bio !== (user?.bio || '')) data.bio = bio;
      if (picture !== (user?.picture || '')) data.picture = picture;

      if (Object.keys(data).length === 0) {
        toast.info(t('editProfile.noChanges'));
        setLoading(false);
        return;
      }

      const res = await userAPI.updateProfile(data);
      updateUser(res.data);
      toast.success(t('editProfile.updated'));
      navigate(`/profile/${user?.user_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('editProfile.back')}
        </button>

        <div className="bg-white rounded-2xl border border-slate-200 p-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('editProfile.title')}</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Avatar Preview */}
            <div className="flex items-center gap-4">
              <Avatar className="w-20 h-20 border-4 border-white shadow-lg">
                <AvatarImage src={picture} alt={name} />
                <AvatarFallback className="text-xl bg-orange-100 text-orange-700">
                  {getAvatarFallback(name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('editProfile.pictureUrl')}
                </label>
                <Input
                  value={picture}
                  onChange={(e) => setPicture(e.target.value)}
                  placeholder="https://example.com/avatar.jpg"
                />
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('editProfile.displayName')}
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                minLength={2}
                maxLength={50}
                required
              />
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('editProfile.bio')}
              </label>
              <Textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder={t('editProfile.bioPlaceholder')}
                className="min-h-[120px] resize-none"
                maxLength={500}
              />
              <p className="text-xs text-slate-400 mt-1">{bio.length}/500</p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4">
              <Button type="submit" disabled={loading} className="btn-boost">
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Save className="w-4 h-4 mr-1" />
                )}
                {t('editProfile.saveChanges')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                {t('editProfile.cancel')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
